/**
 * CollectionCards — horizontal shelf of collections on the homepage.
 * Native scroll with snap points; the arrow buttons scroll one view at a time.
 */
import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Image } from "@shopify/hydrogen-react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import {
  CardArrow,
  cardMedia,
  cardSurface,
} from "@modules/shared/Card/cardStyles";
import {
  SectionHeader,
  SectionLinkLabel,
  sectionLinkClass,
} from "./SectionHeader";

interface CategoryItem {
  id: string;
  handle: string;
  title: string;
  description: string;
  image?: {
    url: string;
    altText?: string | null;
  } | null;
}

interface CollectionCardsProps {
  locale: string;
  collections: Array<CategoryItem>;
}

const arrowClass =
  "flex size-10 items-center justify-center rounded-full bg-background-7 text-text-1 outline-1 -outline-offset-1 outline-black/10 transition-[background-color,scale] duration-100 ease-out hover:bg-background-5 focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.94] max-mobile:hidden";

export function CollectionCards({
  locale,
  collections,
}: Readonly<CollectionCardsProps>) {
  const { t } = useTranslation();
  const shelf = useRef<HTMLUListElement>(null);

  const scrollBy = (direction: 1 | -1) => {
    const el = shelf.current;
    if (!el) return;
    const smooth = !globalThis.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    el.scrollBy({
      left: direction * el.clientWidth * 0.9,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  return (
    <section className="px-4 py-14 sm:px-6 lg:px-8 max-mobile:py-10">
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          title={t("Collections.title")}
          link={
            <Link
              to="/$locale/collections"
              params={{ locale }}
              className={sectionLinkClass}
            >
              <SectionLinkLabel label={t("Homepage.viewAll")} />
            </Link>
          }
        >
          <button
            type="button"
            className={arrowClass}
            onClick={() => scrollBy(-1)}
            aria-label={t("Common.previous")}
          >
            <ChevronLeft className="size-5" strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            className={arrowClass}
            onClick={() => scrollBy(1)}
            aria-label={t("Common.next")}
          >
            <ChevronRight className="size-5" strokeWidth={2} aria-hidden />
          </button>
        </SectionHeader>

        <ul
          ref={shelf}
          data-reveal="items"
          className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pt-1 pb-6 [scrollbar-width:none] sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:mx-0 lg:scroll-px-0 lg:px-0 [&::-webkit-scrollbar]:hidden"
        >
          {collections.map((collection) => (
            <li
              key={collection.id}
              className="w-[calc((100%-3*1rem)/4)] shrink-0 snap-start max-mobile:w-[46%] sm:max-mobile:w-[31%]"
            >
              <Link
                to="/$locale/collections/$handle"
                params={{ locale, handle: collection.handle }}
                className={`${cardSurface} h-full`}
              >
                <div className={`${cardMedia} aspect-square`}>
                  {collection.image?.url ? (
                    <Image
                      src={collection.image.url}
                      alt=""
                      className="size-full object-contain p-5 max-mobile:p-2 transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:transition-none"
                      loading="lazy"
                      sizes="(min-width: 1110px) 25vw, 70vw"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-background-6">
                      <ShoppingBag
                        className="size-8 text-text-4"
                        strokeWidth={1.5}
                        aria-hidden
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-1 items-center justify-between gap-3 px-2 pt-3 pb-1.5">
                  <h3 className="text-base font-bold text-balance text-text-1 max-mobile:text-small">
                    {collection.title}
                  </h3>
                  <CardArrow className="max-mobile:hidden" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
